import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Briefcase, 
  Users, 
  Bot, 
  Globe, 
  Smartphone, 
  CheckCircle2, 
  Coins,
  ChevronRight,
  Loader2,
  AlertCircle,
  RefreshCw,
  Megaphone
} from 'lucide-react';
import { tasksApi, Task } from '../../api';
import { useTelegram } from '../../hooks';
import { useAppStore } from '../../store/useAppStore';
import { TaskModal } from './TaskModal';

// Task type icons and labels - compact config
const TASK_TYPE_CONFIG: Record<string, { icon: React.ElementType; color: string }> = {
  telegram_channel: { icon: Users, color: 'bg-blue-500' },
  telegram_group: { icon: Users, color: 'bg-green-500' },
  telegram_bot: { icon: Bot, color: 'bg-purple-500' },
  telegram_miniapp: { icon: Smartphone, color: 'bg-orange-500' },
  website: { icon: Globe, color: 'bg-cyan-500' },
  social_like: { icon: CheckCircle2, color: 'bg-pink-500' },
  social_share: { icon: Globe, color: 'bg-indigo-500' },
  social_visit: { icon: Globe, color: 'bg-teal-500' },
};

export function Tasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [todayCompleted, setTodayCompleted] = useState(0);
  const [maxDailyTasks, setMaxDailyTasks] = useState(50);
  const [refreshing, setRefreshing] = useState(false);

  const { hapticFeedback, hapticNotification } = useTelegram();
  const updateBalance = useAppStore((state) => state.updateBalance);

  const fetchTasks = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setRefreshing(!showLoader);
      
      const response = await tasksApi.getTasks();
      
      if (response.success) {
        setTasks(response.tasks);
        setTodayCompleted(response.todayCompleted);
        setMaxDailyTasks(response.maxDailyTasks);
        setError(null);
      } else {
        setError('Failed to load tasks');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleRefresh = () => {
    hapticFeedback('light');
    fetchTasks(false);
  };

  const handleTaskClick = (task: Task) => {
    hapticFeedback('medium');
    setSelectedTask(task);
  };

  const handleTaskComplete = (_reward: number, newBalance: number) => {
    hapticNotification('success');
    updateBalance(newBalance);
    setTodayCompleted(prev => prev + 1);
    setTasks(prev => prev.filter(t => t.id !== selectedTask?.id));
    setSelectedTask(null);
  };

  const handleModalClose = () => {
    setSelectedTask(null);
    fetchTasks(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
        <p className="text-white/60 text-sm">Loading tasks...</p>
      </div>
    );
  }

  return (
    <div className="p-3 pb-24">
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-primary" />
          <h1 className="text-lg font-bold text-white">Tasks</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/50 bg-white/10 px-2 py-1 rounded-lg">
            {todayCompleted}/{maxDailyTasks} today
          </span>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 rounded-lg bg-white/10"
          >
            <RefreshCw className={`w-4 h-4 text-white/70 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="flex gap-2 mb-3 overflow-x-auto">
        <div className="flex-shrink-0 bg-primary/20 rounded-lg px-3 py-2 flex items-center gap-2">
          <span className="text-primary font-bold">{tasks.length}</span>
          <span className="text-xs text-white/60">Available</span>
        </div>
        <div className="flex-shrink-0 bg-green-500/20 rounded-lg px-3 py-2 flex items-center gap-2">
          <span className="text-green-400 font-bold">{todayCompleted}</span>
          <span className="text-xs text-white/60">Done</span>
        </div>
        {maxDailyTasks - todayCompleted <= 5 && maxDailyTasks - todayCompleted > 0 && (
          <div className="flex-shrink-0 bg-yellow-500/20 rounded-lg px-3 py-2 flex items-center gap-2">
            <AlertCircle className="w-3 h-3 text-yellow-400" />
            <span className="text-xs text-yellow-400">{maxDailyTasks - todayCompleted} left</span>
          </div>
        )}
      </div>

      {/* Advertise Banner - Compact */}
      <Link
        to="/advertiser"
        className="block bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/20 rounded-xl p-3 mb-3"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Megaphone className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-white text-sm">Advertise Here</p>
            <p className="text-xs text-white/50">Promote to thousands of users</p>
          </div>
          <ChevronRight className="w-4 h-4 text-white/30" />
        </div>
      </Link>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <p className="text-red-400 text-sm flex-1">{error}</p>
          <button onClick={() => fetchTasks()} className="text-xs text-red-300 underline">Retry</button>
        </div>
      )}

      {/* No Tasks State */}
      {!error && tasks.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-3">
            <Briefcase className="w-8 h-8 text-white/40" />
          </div>
          <h3 className="font-semibold text-white mb-1">No Tasks Available</h3>
          <p className="text-white/50 text-sm max-w-xs">
            {todayCompleted >= maxDailyTasks 
              ? "Daily limit reached. Come back tomorrow!"
              : "New tasks added regularly. Check back soon!"}
          </p>
          <button onClick={handleRefresh} className="mt-3 px-4 py-2 bg-primary/20 text-primary rounded-lg text-sm flex items-center gap-2">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      )}

      {/* Compact Task Cards */}
      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {tasks.map((task, index) => {
            const config = TASK_TYPE_CONFIG[task.type] || { icon: Globe, color: 'bg-gray-500' };
            const Icon = config.icon;

            return (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleTaskClick(task)}
                className="relative bg-surface rounded-xl p-3 cursor-pointer active:scale-[0.98] border border-white/5 hover:border-primary/20 transition-all"
              >
                {/* Boosted Badge */}
                {task.isBoosted && (
                  <div className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white">
                    ⚡ HOT
                  </div>
                )}

                <div className="flex items-center gap-3">
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center flex-shrink-0`}>
                    {task.thumbnailUrl ? (
                      <img src={task.thumbnailUrl} alt="" className="w-full h-full rounded-lg object-cover" />
                    ) : (
                      <Icon className="w-5 h-5 text-white" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white text-sm truncate">{task.title}</h3>
                    <p className="text-xs text-white/50 truncate">{task.description}</p>
                  </div>

                  {/* Reward */}
                  <div className="flex items-center gap-1 bg-primary/20 rounded-lg px-2 py-1">
                    <Coins className="w-3 h-3 text-primary" />
                    <span className="text-sm font-bold text-primary">{task.reward}</span>
                  </div>
                  
                  <ChevronRight className="w-4 h-4 text-white/20" />
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Task Modal */}
      <TaskModal task={selectedTask} onClose={handleModalClose} onComplete={handleTaskComplete} />
    </div>
  );
}
